import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Common/Card';
import api from '../utils/api';
import {
  TrendingUp,
  Home,
  Calendar,
  Cake,
  Activity,
  DollarSign,
  Users,
  FileText,
  Clock,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatStatus } from '../utils/formatters';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Dashboard verisi yüklenemedi:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const metrics = dashboardData?.metrics || {};

  // Chart verileri
  const salesChartData = [
    { name: 'Pzt', satis: 4, gorusme: 12 },
    { name: 'Sal', satis: 3, gorusme: 15 },
    { name: 'Çar', satis: 5, gorusme: 18 },
    { name: 'Per', satis: 7, gorusme: 14 },
    { name: 'Cum', satis: 6, gorusme: 20 },
    { name: 'Cmt', satis: 2, gorusme: 8 },
    { name: 'Paz', satis: 1, gorusme: 5 },
  ];

  const portfolioStatusData = [
    { name: 'Satışta', value: 45, color: '#10b981' },
    { name: 'Kaporalı', value: 15, color: '#f59e0b' },
    { name: 'Satıldı', value: 30, color: '#ef4444' },
    { name: 'Opsiyonlu', value: 10, color: '#3b82f6' },
  ];

  const recentActivities = [
    { type: 'sale', user: 'Ahmet Yılmaz', action: 'Yeni satış ekledi', target: '3+1 Deniz Manzaralı Daire', time: '5 dakika önce', icon: DollarSign, color: 'text-green-600 bg-green-100' },
    { type: 'meeting', user: 'Ayşe Demir', action: 'Görüşme tamamladı', target: 'Mehmet Kaya', time: '15 dakika önce', icon: Users, color: 'text-blue-600 bg-blue-100' },
    { type: 'portfolio', user: 'Admin', action: 'Yeni portföy ekledi', target: '2+1 Merkezi Daire', time: '1 saat önce', icon: Home, color: 'text-purple-600 bg-purple-100' },
    { type: 'meeting', user: 'Ahmet Yılmaz', action: 'Görüşme planladı', target: 'Zeynep Şahin', time: '2 saat önce', icon: Calendar, color: 'text-orange-600 bg-orange-100' },
    { type: 'customer', user: 'Ayşe Demir', action: 'Yeni müşteri ekledi', target: 'Ali Veli', time: '3 saat önce', icon: Users, color: 'text-indigo-600 bg-indigo-100' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Hoş geldiniz, işte bugünün özeti</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500 dark:text-gray-400">Son güncelleme</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{new Date().toLocaleString('tr-TR')}</p>
        </div>
      </div>

      {/* Doğum Günü Uyarısı */}
      {dashboardData?.birthday_customers && dashboardData.birthday_customers.length > 0 && (
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 border-l-4 border-pink-500 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Cake className="h-6 w-6 text-pink-600 dark:text-pink-400" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-semibold text-pink-900 dark:text-pink-100">
                🎉 Bugün Doğum Günü Olan Müşteriler
              </h3>
              <div className="mt-2 text-sm text-pink-800 dark:text-pink-200">
                {dashboardData.birthday_customers.map((customer, index) => (
                  <span key={customer.id}>
                    <button
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      className="font-medium hover:underline"
                    >
                      {customer.name} {customer.surname}
                    </button>
                    {index < dashboardData.birthday_customers.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metrik Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Günlük Görüşmeler</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{metrics.daily_meetings || 0}</p>
              {metrics.meetings_trend !== undefined && (
                <p className={`text-xs flex items-center mt-1 font-medium ${metrics.meetings_trend >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                  {metrics.meetings_trend >= 0 ? (
                    <ArrowUpRight size={14} className="mr-1" />
                  ) : (
                    <ArrowDownRight size={14} className="mr-1" />
                  )}
                  {metrics.meetings_trend >= 0 ? '+' : ''}{metrics.meetings_trend}% bu hafta
                </p>
              )}
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-xl">
              <Calendar size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Günlük Satışlar</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{metrics.daily_sales || 0}</p>
              {metrics.sales_trend !== undefined && (
                <p className={`text-xs flex items-center mt-1 font-medium ${metrics.sales_trend >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                  {metrics.sales_trend >= 0 ? (
                    <ArrowUpRight size={14} className="mr-1" />
                  ) : (
                    <ArrowDownRight size={14} className="mr-1" />
                  )}
                  {metrics.sales_trend >= 0 ? '+' : ''}{metrics.sales_trend}% bu hafta
                </p>
              )}
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-xl">
              <TrendingUp size={24} className="text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Aktif Portföyler</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{metrics.weekly_portfolios || 0}</p>
              {metrics.portfolios_trend !== undefined && (
                <p className={`text-xs flex items-center mt-1 font-medium ${metrics.portfolios_trend >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                  {metrics.portfolios_trend >= 0 ? (
                    <ArrowUpRight size={14} className="mr-1" />
                  ) : (
                    <ArrowDownRight size={14} className="mr-1" />
                  )}
                  {metrics.portfolios_trend >= 0 ? '+' : ''}{metrics.portfolios_trend}% bu hafta
                </p>
              )}
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-xl">
              <Home size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Toplam Müşteri</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{metrics.total_customers || 0}</p>
              {metrics.customers_trend !== undefined && (
                <p className={`text-xs flex items-center mt-1 font-medium ${metrics.customers_trend >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                  {metrics.customers_trend >= 0 ? (
                    <ArrowUpRight size={14} className="mr-1" />
                  ) : (
                    <ArrowDownRight size={14} className="mr-1" />
                  )}
                  {metrics.customers_trend >= 0 ? '+' : ''}{metrics.customers_trend}% bu ay
                </p>
              )}
            </div>
            <div className="bg-orange-100 dark:bg-orange-900/30 p-3 rounded-xl">
              <Users size={24} className="text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Chart'lar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Satış ve Görüşme Trendi */}
        <Card title="Haftalık Satış ve Görüşme Trendi">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={salesChartData}>
              <defs>
                <linearGradient id="colorSatis" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorGorusme" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f0f0f0'} />
              <XAxis dataKey="name" stroke="#888" tick={{ fill: '#888' }} />
              <YAxis stroke="#888" tick={{ fill: '#888' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: document.documentElement.classList.contains('dark') ? '#1f2937' : '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827'
                }}
                itemStyle={{ color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827' }}
              />
              <Legend />
              <Area type="monotone" dataKey="satis" stroke="#10b981" fillOpacity={1} fill="url(#colorSatis)" name="Satış" />
              <Area type="monotone" dataKey="gorusme" stroke="#3b82f6" fillOpacity={1} fill="url(#colorGorusme)" name="Görüşme" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Portföy Durumu */}
        <Card title="Portföy Durumu Dağılımı">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={portfolioStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {portfolioStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Son Aktiviteler ve Listeler */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Son Yapılan İşler */}
        <Card title="Son Yapılan İşler" className="lg:col-span-2">
          <div className="space-y-3">
            {recentActivities.map((activity, index) => {
              const Icon = activity.icon;
              return (
                <div key={index} className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors">
                  <div className={`${activity.color} dark:bg-gray-700 p-2 rounded-lg flex-shrink-0`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {activity.user} <span className="font-normal text-gray-600 dark:text-gray-400">{activity.action}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{activity.target}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{activity.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Hızlı İstatistikler */}
        <Card title="Hızlı İstatistikler">
          <div className="space-y-3">
            <button
              onClick={() => navigate('/meetings')}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-800/30 dark:hover:to-blue-700/30 rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 p-2 rounded-lg">
                  <FileText size={18} className="text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Bekleyen Görüşmeler</span>
              </div>
              <span className="text-xl font-bold text-blue-600 dark:text-blue-400">{metrics.pending_meetings || 0}</span>
            </button>

            <button
              onClick={() => navigate('/sales')}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 hover:from-green-100 hover:to-green-200 dark:hover:from-green-800/30 dark:hover:to-green-700/30 rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-center gap-3">
                <div className="bg-green-500 p-2 rounded-lg">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Bu Ay Satışlar</span>
              </div>
              <span className="text-xl font-bold text-green-600 dark:text-green-400">{metrics.monthly_sales || 0}</span>
            </button>

            <button
              onClick={() => navigate('/portfolios')}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 hover:from-purple-100 hover:to-purple-200 dark:hover:from-purple-800/30 dark:hover:to-purple-700/30 rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-center gap-3">
                <div className="bg-purple-500 p-2 rounded-lg">
                  <Home size={18} className="text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Yeni Portföyler</span>
              </div>
              <span className="text-xl font-bold text-purple-600 dark:text-purple-400">{metrics.new_portfolios || 0}</span>
            </button>

            <button
              onClick={() => navigate('/meetings')}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 hover:from-orange-100 hover:to-orange-200 dark:hover:from-orange-800/30 dark:hover:to-orange-700/30 rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-center gap-3">
                <div className="bg-orange-500 p-2 rounded-lg">
                  <AlertCircle size={18} className="text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Takip Gerekli</span>
              </div>
              <span className="text-xl font-bold text-orange-600 dark:text-orange-400">{metrics.needs_follow_up || 0}</span>
            </button>
          </div>
        </Card>
      </div>


      {/* Son Satışlar ve Görüşmeler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Son Satışlar */}
        <Card title="Son Satışlar" noPadding>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {dashboardData?.recent_sales && dashboardData.recent_sales.length > 0 ? (
              dashboardData.recent_sales.map((sale) => (
                <button
                  key={sale.id}
                  onClick={() => navigate(`/sales`)}
                  className="w-full p-4 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white dark:hover:from-gray-700/50 dark:hover:to-gray-800 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">{sale.portfolio?.title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1 mt-1">
                        <Users size={14} />
                        {sale.customer?.name} {sale.customer?.surname}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(sale.sale_date).toLocaleDateString('tr-TR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600 dark:text-green-400 text-lg">
                        {sale.final_sale_price?.toLocaleString('tr-TR')} ₺
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">Satış Fiyatı</span>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <TrendingUp size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p>Henüz satış kaydı yok</p>
              </div>
            )}
          </div>
        </Card>

        {/* Son Görüşmeler */}
        <Card title="Son Görüşmeler" noPadding>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {dashboardData?.recent_meetings && dashboardData.recent_meetings.length > 0 ? (
              dashboardData.recent_meetings.map((meeting) => (
                <button
                  key={meeting.id}
                  onClick={() => navigate(`/meetings/${meeting.id}`)}
                  className="w-full p-4 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white dark:hover:from-gray-700/50 dark:hover:to-gray-800 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors flex items-center gap-2">
                        <Users size={16} />
                        {meeting.customer?.name} {meeting.customer?.surname}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">👤 {meeting.user?.name}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(meeting.date).toLocaleDateString('tr-TR')}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${meeting.is_completed
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                        : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white'
                        }`}>
                        {formatStatus(meeting.type)}
                      </span>
                      {meeting.is_completed && (
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Tamamlandı</span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Calendar size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p>Henüz görüşme kaydı yok</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
